/* eslint-disable camelcase */
/* global G, PLATFORM */
import routeComposition from 'trait/composition/route'
import seqSessionCookieLogout from '@gaia/sequence/app/session/cookie/logout'
import seqModuleAdapterSessionStateNavigationReset from '@gaia/sequence/module/adapter/session/navigation'
import seqResetAcl from '@gaia/sequence/app/acl/reset'
import seqDisconnectPubSub from 'lib/sequence/app/pubsub/disconnect'
import seqCleanAttachmentCache from 'lib/sequence/app/init/cleanAttachmentCache'
import settings from '@tenant/settings'

/**
 * Resets the `lastContext` settings.
 *
 * This needs to be down before calling {@link seqSessionCookieLogout}.
 *
 * @param {Gaia.AppModule.Spec} module  the application's current module
 */
const resetLastContext = async (module) => {
  const settingsAdapter = module[G.ADAPTER][G.SETTINGS]
  const context = module?.[G.SESSION]?.[G.STATE]?.[G.CONTEXT]
      || module?.[G.ADAPTER]?.[G.SESSION]?.[G.STATE]?.[G.CONTEXT]

  // Only attempt to reset the context if we had a valid context before
  !!context && context !== 'pre' && await settingsAdapter[G.API].set({ key: 'lastContext', value: null })
}

/**
 * Asks the notifications adapter to clear the current user's list of notifications.
 *
 * @param {Gaia.AppModule.Spec} module  the application's current module
 */
const clearNotifications = (module) => {
  const eventBus = module[G.ADAPTER][G.EVENTS]

  const eventType = eventBus.type(G.NOTIFICATION, G.SET, 'alert')
  const detail = { page: null, data: null }
  eventBus.dispatch(eventType, detail)
}

/**
 * If the `oauth` setting is enabled for the current tenant and the current model contains an
 * `oauth` property, this method uses the data of said property to attempt to redirect the user
 * to the OAuth page and end their session there.
 *
 * @param {Gaia.AppModule.Spec} module  the application's current module
 * @returns {Promise<void>}
 */
const endOAuthSession = async (module) => {
  const model = module[G.MODEL]
  const modelState = model[G.STATE]
  const { oauth } = modelState

  if (settings?.oauth?.enabled && oauth) {
    const {
      authz_url: url,
      oidc_payload: {
        id_token_hint,
        [`post_logout_redirect_uri_${PLATFORM}`]: post_logout_redirect_uri,
      },
    } = oauth

    delete modelState.oauth

    const params = {
      id_token_hint,
      post_logout_redirect_uri,
    }

    if (PLATFORM === 'web') {
      module[G.ADAPTER][G.ROUTER].open({ url, params })
    } else if (PLATFORM === 'mobile') {
      // const moduleState = module[G.STATE]
      // moduleState[G.ROUTE] = routeComposition('guest', 'external')
      // await redirectSequence(module)({ url, params })
      try {
        await model[G.ADAPTER][G.HTTP].get({ url, params })
      } catch (e) {
        console.warn('OAuth Logout Failed: ', e)
      }
    }
  }
}

/**
 * Removes cookies that were explicitly persisted as a workaround to a bug in iOS that prevents them
 * from surviving an application restart.
 *
 * TODO: Find a better solution.
 *
 * @param {Gaia.AppModule.Spec} module  the application's current module
 * @returns {Promise<void>}
 */
const removePersistedCookies = async (module) => {
  if (PLATFORM === 'mobile') {
    try {
      const httpAdapter = module[G.MODEL][G.ADAPTER][G.HTTP]
      await httpAdapter.clearCookies()

      const storage = module[G.ADAPTER][G.STORAGE]
      await storage.remove('session')
      await storage.remove('session.sig')
    } catch (e) {
      console.error('Failed to delete persisted cookies:', e)
    }
  }
}

const action = module => () => async (...args) => {
  // reset last context
  await resetLastContext(module)
  // disconnect to pubsub channels
  seqDisconnectPubSub(module, null)
  // invalidate cookie
  await seqSessionCookieLogout(module)
  // reset nav stack
  seqModuleAdapterSessionStateNavigationReset(module, [])
  // reset acl manager
  seqResetAcl(module, null)

  // Clean attachment cache
  seqCleanAttachmentCache(module)()
  // clear notifications
  clearNotifications(module)
  // create module.state.route
  const moduleState = module[G.STATE]

  // deleting all persisted data
  !settings.suppressPersistence && await module[G.ADAPTER][G.PERSISTENCE].clear()
  // remove mobile's persisted session cookies
  await removePersistedCookies(module)
  // remove storage's impersonation entry
  await module[G.ADAPTER][G.STORAGE].remove('imp')
  // call plugins' logout lifecycle method
  await module[G.ADAPTER][G.PLUGIN].logout()
  // close oauth session, if any
  await endOAuthSession(module)
  // eslint-disable-next-line no-undefined
  moduleState[G.ROUTE] = routeComposition(undefined, 'index')
  // clearing any errors, to make sure we can go to the route set above
  moduleState[G.ERROR] = false
  // set default context
  module[G.ADAPTER][G.ROUTER].defaultContext()
  // set default language
  await module[G.ADAPTER][G.INTL].defaultLanguage()
  // let hook redirect, do its magic
  return args
}

export default action
